(() => {
  const initialized = new WeakSet();

  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  };

  const cleanRegistration = (value) =>
    String(value || '').replace(/\s+/g, '').toUpperCase();

  const formatDate = (value) => {
    if (!value) return 'Not available';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  const formatMileage = (test) => {
    if (!test?.odometerValue) return 'Not recorded';

    const value = Number(test.odometerValue);
    const mileage = Number.isFinite(value)
      ? new Intl.NumberFormat('en-GB').format(value)
      : test.odometerValue;

    return `${mileage} ${test.odometerUnit || ''}`.trim();
  };

  const addDefinition = (list, label, value) => {
    const wrapper = create('div');
    wrapper.append(
      create('dt', '', label),
      create('dd', '', value || 'Not available')
    );
    list.append(wrapper);
  };

  const addFact = (container, label, value) => {
    const wrapper = create('div', 'mot-test__fact');
    wrapper.append(
      create('small', '', label),
      create('strong', '', value || 'Not available')
    );
    container.append(wrapper);
  };

  const renderTest = (test, index) => {
    const result = String(test.testResult || 'UNKNOWN').toUpperCase();
    const details = create('details', 'mot-test');
    details.dataset.result = result.toLowerCase();

    if (index === 0) details.open = true;

    const summary = create('summary');
    const summaryGrid = create('div', 'mot-test__summary');

    const resultNode = create('div', 'mot-test__result', result);
    const dateNode = create(
      'div',
      'mot-test__meta',
      `Tested ${formatDate(test.completedDate)}`
    );
    const mileageNode = create(
      'div',
      'mot-test__mileage',
      formatMileage(test)
    );

    summaryGrid.append(resultNode, dateNode, mileageNode);
    summary.append(summaryGrid);
    details.append(summary);

    const body = create('div', 'mot-test__body');
    const facts = create('div', 'mot-test__facts');

    addFact(facts, 'Test date', formatDate(test.completedDate));
    addFact(
      facts,
      'Expiry date',
      test.expiryDate ? formatDate(test.expiryDate) : 'Not applicable'
    );
    addFact(facts, 'Mileage', formatMileage(test));

    body.append(facts);

    const defects = Array.isArray(test.defects) ? test.defects : [];

    if (defects.length) {
      const heading = create('h3', '', 'Recorded defects and advisories');
      const list = create('ul', 'mot-test__defects');

      defects.forEach((defect) => {
        const item = create('li');
        const type = create(
          'span',
          'mot-test__defect-type',
          `${defect.type || 'NOTICE'}: `
        );
        const text = document.createTextNode(defect.text || 'No description');
        item.append(type, text);
        list.append(item);
      });

      body.append(heading, list);
    } else {
      body.append(
        create(
          'p',
          'mot-test__empty',
          'No defects or advisories are recorded for this test.'
        )
      );
    }

    details.append(body);
    return details;
  };

  const errorMessage = (status, payload) => {
    if (status === 404 || payload?.error === 'not_found') {
      return 'No MOT history was found for that registration. Check the registration and try again.';
    }

    if (status === 400 || payload?.error === 'invalid_registration') {
      return 'Enter a valid UK vehicle registration.';
    }

    if (status === 429 || payload?.error === 'rate_limited') {
      return 'Too many checks have been made. Please try again shortly.';
    }

    if (status >= 500) {
      return 'The MOT service is temporarily unavailable. Please try again later.';
    }

    return 'We could not complete the MOT check. Please try again.';
  };

  const renderVehicle = (root, data) => {
    const results = root.querySelector('[data-mot-results]');
    const title = root.querySelector('[data-mot-vehicle-title]');
    const status = root.querySelector('[data-mot-status]');
    const details = root.querySelector('[data-mot-vehicle-details]');
    const history = root.querySelector('[data-mot-history]');
    const count = root.querySelector('[data-mot-test-count]');

    const tests = Array.isArray(data.motTests)
      ? [...data.motTests].sort(
          (a, b) =>
            new Date(b.completedDate || 0).getTime() -
            new Date(a.completedDate || 0).getTime()
        )
      : [];

    const latest = tests[0];
    const latestResult = String(latest?.testResult || 'UNKNOWN').toUpperCase();

    title.textContent =
      [data.make, data.model, data.registration].filter(Boolean).join(' | ') ||
      'Vehicle';

    const expiryDate = latest?.expiryDate
      ? new Date(`${latest.expiryDate}T23:59:59Z`)
      : null;

    const isExpired =
      latestResult === 'PASSED' &&
      expiryDate &&
      !Number.isNaN(expiryDate.getTime()) &&
      expiryDate.getTime() < Date.now();

    status.textContent =
      isExpired
        ? 'MOT expired'
        : latestResult === 'PASSED'
          ? 'MOT passed'
          : latestResult === 'FAILED'
            ? 'Latest MOT failed'
            : 'MOT status unavailable';

    status.dataset.result =
      isExpired ? 'expired' : latestResult.toLowerCase();

    details.replaceChildren();

    addDefinition(details, 'Registration', data.registration);
    addDefinition(details, 'Make', data.make);
    addDefinition(details, 'Model', data.model);
    addDefinition(details, 'Fuel', data.fuelType);
    addDefinition(details, 'Colour', data.primaryColour);
    addDefinition(
      details,
      'Engine size',
      data.engineSize ? `${data.engineSize} cc` : null
    );
    addDefinition(
      details,
      'First used',
      data.firstUsedDate ? formatDate(data.firstUsedDate) : null
    );
    addDefinition(details, 'Outstanding recall', data.hasOutstandingRecall);

    history.replaceChildren();

    tests.forEach((test, index) => {
      history.append(renderTest(test, index));
    });

    count.textContent =
      `${tests.length} MOT ${tests.length === 1 ? 'record' : 'records'}`;

    results.hidden = false;
    results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const init = (root) => {
    if (initialized.has(root)) return;
    initialized.add(root);

    const form = root.querySelector('[data-mot-form]');
    const input = root.querySelector('[data-mot-registration]');
    const submit = root.querySelector('[data-mot-submit]');
    const message = root.querySelector('[data-mot-message]');
    const results = root.querySelector('[data-mot-results]');
    const endpoint = root.dataset.endpoint || '/apps/mot-check';

    if (!form || !input || !submit || !message || !results) return;

    input.addEventListener('input', () => {
      input.value = input.value.toUpperCase();
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const rawRegistration = String(input.value || '').trim();
      const registration = cleanRegistration(rawRegistration);

      message.textContent = '';
      message.dataset.state = '';
      results.hidden = true;

      const hasInvalidCharacters = !/^[A-Za-z0-9 ]+$/.test(rawRegistration);

      if (
        hasInvalidCharacters ||
        !/^[A-Z0-9]{1,8}$/.test(registration)
      ) {
        message.textContent = 'Enter a valid UK vehicle registration.';
        message.dataset.state = 'error';
        input.focus();
        return;
      }

      submit.disabled = true;
      submit.textContent = 'CheckingÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â¦';
      form.setAttribute('aria-busy', 'true');
      message.textContent = 'Checking DVSA MOT recordsÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â¦';
      message.dataset.state = 'loading';

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ registration })
        });

        let payload = null;

        try {
          payload = await response.json();
        } catch (_) {
          payload = null;
        }

        if (!response.ok || !payload?.ok || !payload?.data) {
          throw {
            motRequest: true,
            status: response.status,
            payload
          };
        }

        const returnedRegistration = cleanRegistration(payload.data.registration);

        if (returnedRegistration !== registration) {
          throw {
            motRequest: true,
            status: 502,
            payload: { error: 'registration_mismatch' }
          };
        }

        message.textContent = `MOT history found for ${registration}.`;
        message.dataset.state = '';
        renderVehicle(root, payload.data);
      } catch (error) {
        const status = error?.motRequest ? error.status : 0;
        const payload = error?.motRequest ? error.payload : null;

        message.textContent = errorMessage(status, payload);
        message.dataset.state = 'error';
      } finally {
        submit.disabled = false;
        submit.textContent = 'Check MOT history';
        form.removeAttribute('aria-busy');
      }
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-mot-checker]').forEach(init);
  };

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  initAll();
})();